import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaQrcode, FaHandshake, FaDownload, FaBuilding, FaSearch, FaUsers } from 'react-icons/fa';

interface MemberConnectionStat {
  user_id: number;
  name: string;
  email: string;
  number: string;
  company_name: string;
  designation: string;
  business_classification: string;
  role: string;
  scanned_count: number;
  connections_count: number;
  chapter_name: string;
}

const TotalConnectionsReport: React.FC = () => {
  const [members, setMembers] = useState<MemberConnectionStat[]>([]);
  const [chapters, setChapters] = useState<any[]>([]);
  const [selectedChapterId, setSelectedChapterId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchChapters();
    fetchReport('');
  }, []);

  const fetchChapters = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/chapters');
      if (res.data.success) {
        setChapters(res.data.chapters || []);
      }
    } catch (err) {
      console.error('Error fetching chapters:', err);
    }
  };

  const fetchReport = async (chapId: string) => {
    try {
      setLoading(true);
      const url = chapId
        ? `https://rmbbackend.rmbjaipur.com/auth/reports/total-connections?chapter_id=${chapId}`
        : 'https://rmbbackend.rmbjaipur.com/auth/reports/total-connections';
      const res = await axios.get(url);
      if (res.data.success) {
        setMembers(res.data.members || []);
      }
    } catch (err) {
      console.error('Error fetching total connections report:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChapterChange = (chapId: string) => {
    setSelectedChapterId(chapId);
    fetchReport(chapId);
  };

  const filteredMembers = members.filter(m => {
    const q = searchQuery.toLowerCase();
    return (
      (m.name || '').toLowerCase().includes(q) ||
      (m.company_name || '').toLowerCase().includes(q) ||
      (m.email || '').toLowerCase().includes(q) ||
      (m.chapter_name || '').toLowerCase().includes(q)
    );
  });

  const totalScansAll = filteredMembers.reduce((sum, m) => sum + (m.scanned_count || 0), 0);
  const totalConnsAll = filteredMembers.reduce((sum, m) => sum + (m.connections_count || 0), 0);

  const exportCSV = () => {
    const headers = ['Member Name', 'Company Name', 'Classification / Designation', 'Chapter', 'Mobile', 'Email', 'Scanned by Member Count', 'Total Network Connections'];
    const rows = filteredMembers.map(m => [
      `"${m.name || ''}"`,
      `"${m.company_name || ''}"`,
      `"${m.business_classification || m.designation || ''}"`,
      `"${m.chapter_name || ''}"`,
      `"${m.number || ''}"`,
      `"${m.email || ''}"`,
      m.scanned_count || 0,
      m.connections_count || 0
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Total_QR_Connections_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">

      {/* Header Banner */}
      <div className="bg-[#0A2451] rounded-2xl p-6 text-white shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-black flex items-center gap-3 text-[#FDBC12]">
            <FaHandshake /> Total Member Connections & QR Scan Activity Report
          </h2>
          <p className="text-xs text-slate-300 mt-1">
            Track member-to-member QR code scans, reciprocal phone contact exchanges, and networking engagement chapter-wise.
          </p>
        </div>

        <button
          onClick={exportCSV}
          disabled={filteredMembers.length === 0}
          className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold text-xs rounded-xl shadow-lg flex items-center gap-2 transition cursor-pointer"
        >
          <FaDownload /> Export CSV Report
        </button>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl">
            <FaUsers />
          </div>
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Active Members</span>
            <h3 className="text-2xl font-black text-slate-900">{filteredMembers.length}</h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-xl">
            <FaQrcode />
          </div>
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Total QR Scans Performed</span>
            <h3 className="text-2xl font-black text-slate-900">{totalScansAll}</h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl">
            <FaHandshake />
          </div>
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Network Connections Created</span>
            <h3 className="text-2xl font-black text-slate-900">{totalConnsAll}</h3>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-2">
          <FaBuilding className="text-slate-400 text-xs" />
          <span className="text-xs font-bold text-slate-700 uppercase">Chapter Filter:</span>
          <select
            value={selectedChapterId}
            onChange={(e) => handleChapterChange(e.target.value)}
            className="p-2 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 bg-slate-50 focus:ring-2 focus:ring-amber-400 outline-none cursor-pointer"
          >
            <option value="">All Whitelabel Chapters</option>
            {chapters.map(ch => (
              <option key={ch.id} value={ch.id}>
                {ch.name} ({ch.code})
              </option>
            ))}
          </select>
        </div>

        <div className="relative w-full sm:w-72">
          <FaSearch className="absolute left-3 top-3 text-slate-400 text-xs" />
          <input
            type="text"
            placeholder="Search member name or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-4 py-2 border border-slate-300 rounded-xl text-xs font-medium focus:ring-2 focus:ring-amber-400 outline-none"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead className="bg-slate-900 text-slate-200 uppercase text-[10px] tracking-wider font-bold">
              <tr>
                <th className="p-3.5">Member Name</th>
                <th className="p-3.5">Chapter</th>
                <th className="p-3.5">Company & Classification</th>
                <th className="p-3.5 text-center">📷 Scanned by Member</th>
                <th className="p-3.5 text-center">🤝 Network Connections</th>
                <th className="p-3.5 text-right">Role</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-400 font-medium">Loading total connections report...</td>
                </tr>
              ) : filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-400 font-medium">No member records found.</td>
                </tr>
              ) : (
                filteredMembers.map((m) => (
                  <tr key={m.user_id} className="hover:bg-slate-50 transition">
                    <td className="p-3.5 font-bold text-slate-900">
                      <div>{m.name}</div>
                      <div className="text-[10px] text-slate-400 font-normal">{m.email} • {m.number}</div>
                    </td>
                    <td className="p-3.5 font-semibold text-blue-700">{m.chapter_name}</td>
                    <td className="p-3.5">
                      <div className="font-bold text-slate-800">{m.company_name || '-'}</div>
                      <div className="text-[10px] text-amber-600 font-semibold">{m.business_classification || m.designation || 'Member'}</div>
                    </td>
                    <td className="p-3.5 text-center font-black text-amber-600 text-sm">
                      {m.scanned_count}
                    </td>
                    <td className="p-3.5 text-center font-black text-emerald-600 text-sm">
                      {m.connections_count}
                    </td>
                    <td className="p-3.5 text-right">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${m.role === 'super-admin' ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-800'}`}>
                        {m.role}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default TotalConnectionsReport;
